import {createContext, useCallback, useContext} from 'react';
import {ComponentWithRequiredChildren, PropsWithRequiredChildren} from '../react/types';
import {useAppInstallPrompt} from './hooks/useAppInstallPrompt';
import {useStoredAppInstallOutcome} from './hooks/useStoredAppInstallOutcome';
import {InstallOutcome} from './types';

interface AppInstallFunctions {
  dismissPrompt: () => void;
  installApp?: () => Promise<void>;
  installOutcome?: InstallOutcome;
}

/* v8 ignore next -- @preserve */
const AppInstallFunctionsContext = createContext<AppInstallFunctions>({
  dismissPrompt: () => {
    // Defaults to no-op; will be initialized in AppInstallFunctionsProvider
  },
});

export function useAppInstallFunctions(): AppInstallFunctions {
  return useContext(AppInstallFunctionsContext);
}

export const AppInstallFunctionsProvider: ComponentWithRequiredChildren = ({children}: PropsWithRequiredChildren) => {
  const {appInstallPrompt} = useAppInstallPrompt();
  const [installOutcome, setInstallOutcome] = useStoredAppInstallOutcome();

  const dismissPrompt = useCallback(() => setInstallOutcome('dismissed'), [setInstallOutcome]);

  const installApp = useCallback(async () => {
    setInstallOutcome(await appInstallPrompt!());
  }, [appInstallPrompt, setInstallOutcome]);

  return (
    <AppInstallFunctionsContext
      value={{
        dismissPrompt,
        installApp: appInstallPrompt ? installApp : undefined,
        installOutcome: installOutcome ?? undefined,
      }}
    >
      {children}
    </AppInstallFunctionsContext>
  );
};
